import { useCallback, useMemo } from "react";
import type { FilterGroup } from "@/lib/filters";
import { EMPTY_GROUP } from "@/lib/filters";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Storage version for schema compatibility
const STORAGE_VERSION = "v1";

interface OpportunityRelationFiltersState {
  account: FilterGroup;
  touchpoint: FilterGroup;
}

const defaultState: OpportunityRelationFiltersState = {
  account: EMPTY_GROUP,
  touchpoint: EMPTY_GROUP,
};

/**
 * Hook to manage opportunity relation filter state for Account and Touchpoint objects.
 *
 * - Persists state to sessionStorage for tab-specific isolation.
 * - Provides setters and clear helpers for each individual object type.
 * - Used for filtering opportunities based on attributes of related entities.
 */
export function useOpportunityFilterState(scope: string) {
  // Use sessionStorage for tab-specific filter isolation
  const storageKey = `${scope}::relation-filters::${STORAGE_VERSION}`;

  // Persistence layer
  const [state, setState] =
    useTrackedSessionStorage<OpportunityRelationFiltersState>(
      storageKey,
      defaultState,
    );

  // Update helpers
  const updateFilterState = useCallback(
    (updates: Partial<OpportunityRelationFiltersState>) => {
      setState((prev) => ({
        account: updates.account ?? prev?.account ?? EMPTY_GROUP,
        touchpoint: updates.touchpoint ?? prev?.touchpoint ?? EMPTY_GROUP,
      }));
    },
    [setState],
  );

  const setAccountFilters = useCallback(
    (filters: FilterGroup) => updateFilterState({ account: filters }),
    [updateFilterState],
  );

  const setTouchpointFilters = useCallback(
    (filters: FilterGroup) => updateFilterState({ touchpoint: filters }),
    [updateFilterState],
  );

  const clearAllFilters = useCallback(() => {
    setState({
      account: EMPTY_GROUP,
      touchpoint: EMPTY_GROUP,
    });
  }, [setState]);

  // Quick flags for UI badges
  const hasAccountFilters = useMemo(
    () => state?.account?.filters?.length > 0,
    [state.account],
  );
  const hasTouchpointFilters = useMemo(
    () => state?.touchpoint?.filters?.length > 0,
    [state.touchpoint],
  );

  return {
    accountFilters: state?.account ?? EMPTY_GROUP,
    touchpointFilters: state?.touchpoint ?? EMPTY_GROUP,
    setAccountFilters,
    setTouchpointFilters,
    clearAllFilters,
    hasAccountFilters,
    hasTouchpointFilters,
  };
}
